// Memory Search — type-ahead over every past guest + full profile modal.
import React, { useEffect, useRef, useState } from 'react';
import SecretDealButton from './SecretDealButton';
import { Search, X, Loader2, CalendarDays, GitMerge } from 'lucide-react';
import { apiCall } from './_shared';
import { GuestRow } from './GuestMemoryCard';

export const GuestSearchBox = ({ onPick, exclude, align = 'right' }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(() => {
      setBusy(true);
      apiCall(`/api/admin/reception/guest-memory/search?q=${encodeURIComponent(q.trim())}`)
        .then((d) => { setResults(d.results || []); setOpen(true); }).catch(() => setResults([])).finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const items = results.filter((r) => !exclude || r.guest_name.toLowerCase() !== exclude.toLowerCase()).map((r) => (
    <li key={r.guest_name}>
      <button type="button" onMouseDown={(e) => { e.preventDefault(); onPick(r.guest_name); setOpen(false); setQ(''); }}
        className="w-full text-left px-2.5 py-1.5 hover:bg-emerald-50 flex items-center justify-between gap-2" data-testid={`guest-search-result-${r.guest_name.replace(/\s+/g, '-')}`}>
        <span className="text-xs font-semibold text-slate-900 truncate">{r.guest_name}{r.has_memo && <span className="ml-1 text-[9px] font-bold uppercase bg-indigo-100 text-indigo-800 px-1 rounded">memo</span>}</span>
        <span className="text-[10px] text-slate-500 whitespace-nowrap">{r.stays} stay{r.stays === 1 ? '' : 's'}{r.last_stay ? ` · ${r.last_stay}` : ''}</span>
      </button>
    </li>
  ));

  return (
    <div className="relative" data-testid="guest-search">
      <div className="flex items-center gap-1 border rounded-full px-2 py-0.5 bg-white">
        {busy ? <Loader2 className="w-3 h-3 animate-spin text-slate-400" /> : <Search className="w-3 h-3 text-slate-400" />}
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Look up any guest…" className="text-xs outline-none w-36 bg-transparent" data-testid="guest-search-input" />
      </div>
      {open && q.trim().length >= 2 && (
        <ul className={`absolute z-40 ${align === 'left' ? 'left-0' : 'right-0'} mt-1 w-72 bg-white border border-emerald-200 rounded-lg shadow-lg max-h-64 overflow-y-auto`} data-testid="guest-search-results">
          {items.length ? items : <li className="px-2.5 py-2 text-xs text-slate-400" data-testid="guest-search-empty">No guest found</li>}
        </ul>
      )}
    </div>
  );
};

const GuestProfileModal = ({ name, userRole, onClose }) => {
  const [g, setG] = useState(null);
  const [err, setErr] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeMsg, setMergeMsg] = useState('');
  const [title, setTitle] = useState(name);
  const canEdit = ['super_admin', 'admin', 'manager', 'reception'].includes(userRole);
  const load = (n) => apiCall(`/api/admin/reception/guest-memory/profile?name=${encodeURIComponent(n || title)}`).then((d) => { setG(d); setTitle(d.guest_name); }).catch((e) => setErr(e.message));
  useEffect(() => { setG(null); setErr(''); setTitle(name); load(name); }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeInto = async (target) => {
    if (!window.confirm(`Merge "${title}" into "${target}"?\nAll stays, memo and tags will count under ${target}.`)) return;
    setMergeMsg('');
    try {
      const r = await apiCall('/api/admin/reception/guest-memory/merge', { method: 'POST', body: JSON.stringify({ source: title, target }) });
      setMerging(false); setMergeMsg(`Merged into ${target}`); load(target);
      void r;
    } catch (e) { setMergeMsg(e.message); }
  };
  const unmerge = async (alias) => {
    try { await apiCall(`/api/admin/reception/guest-memory/aliases/${encodeURIComponent(alias)}`, { method: 'DELETE' }); setMergeMsg(`Split off ${alias}`); load(); }
    catch (e) { setMergeMsg(e.message); }
  };

  const history = (g?.history || []).map((s) => (
    <li key={s.booking_id} className="flex items-center justify-between text-xs py-1 border-b last:border-0" data-testid={`guest-profile-stay-${s.booking_id}`}>
      <span className="text-slate-800">{s.check_in} → {s.check_out} <span className="text-slate-500">· {s.nights}n</span></span>
      <span className="text-slate-600 truncate mx-2">{s.room_name || '—'}</span>
      <span className="text-slate-500 whitespace-nowrap">{s.total ? `€${Number(s.total).toFixed(0)}` : ''} <span className="text-[9px] uppercase">{s.source}</span></span>
    </li>
  ));
  const upcoming = (g?.upcoming || []).map((u) => (
    <li key={u.booking_id} className="text-xs text-emerald-800" data-testid={`guest-profile-upcoming-${u.booking_id}`}><CalendarDays className="w-3 h-3 inline mr-1" />{u.check_in} → {u.check_out} · {u.room_name || '—'} · {u.status}</li>
  ));

  const aliasChips = (g?.aliases || []).map((a) => (
    <span key={a} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-700 border rounded px-1.5 py-0.5" data-testid={`guest-profile-alias-${a.replace(/\s+/g, '-')}`}>
      also “{a}”
      {canEdit && <button type="button" onClick={() => unmerge(a)} className="text-slate-400 hover:text-red-600" title="Split this spelling back out" data-testid={`guest-profile-unmerge-${a.replace(/\s+/g, '-')}`}><X className="w-3 h-3" /></button>}
    </span>
  ));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="guest-profile-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mt-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b gap-2">
          <h3 className="font-bold text-slate-900 text-sm truncate">Guest memory · {title}</h3>
          <div className="flex items-center gap-2">
            {canEdit && g && (
              <button type="button" onClick={() => { setMerging((m) => !m); setMergeMsg(''); }} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 border rounded-full px-2.5 py-1 hover:bg-slate-50" data-testid="guest-profile-merge-toggle">
                <GitMerge className="w-3 h-3" /> Merge duplicate
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="guest-profile-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {merging && (
            <div className="flex flex-wrap items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="guest-profile-merge-panel">
              <span className="text-amber-900">Same person under another spelling? Pick the profile to merge <b>{title}</b> into:</span>
              <GuestSearchBox onPick={mergeInto} exclude={title} align="left" />
            </div>
          )}
          {mergeMsg && <p className="text-xs font-semibold text-emerald-700" data-testid="guest-profile-merge-msg">{mergeMsg}</p>}
          {aliasChips.length > 0 && <div className="flex flex-wrap gap-1" data-testid="guest-profile-aliases">{aliasChips}</div>}
          {err && <p className="text-sm text-slate-500" data-testid="guest-profile-error">{err}</p>}
          {!g && !err && <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</p>}
          {g && <ul><GuestRow key={title} g={g} date={g.check_in} canEdit={canEdit} onSaved={() => load()} profile /></ul>}
          {g && canEdit && <SecretDealButton guestName={title} phone={g.phone} lang={g.lang} />}
          {upcoming.length > 0 && <div><p className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-1">Upcoming</p><ul className="space-y-0.5">{upcoming}</ul></div>}
          {history.length > 0 && <div><p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Past stays</p><ul data-testid="guest-profile-history">{history}</ul></div>}
        </div>
      </div>
    </div>
  );
};

export default GuestProfileModal;
